// ============================================================================
// Firebase app + Auth singleton.
// Config is loaded from VITE_FIREBASE_* env vars. The web API key is a
// public identifier — security comes from Firebase's authorized-domain list,
// not from hiding the key.
//
// Persistence: we explicitly opt into browserLocalPersistence so the auth
// state survives the OAuth redirect on iOS Safari (Phase 1 of the mobile
// sign-in fix — see auth.tsx for Phase 2 / 3). Without this, iOS storage
// partitioning between accounts.google.com and our app drops the
// pre-redirect state and the bridge call fails with "missing initial state".
// ============================================================================

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  getAuth,
  GoogleAuthProvider,
  setPersistence,
  type Auth,
} from 'firebase/auth';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

if (!config.apiKey || !config.projectId) {
  throw new Error(
    'Missing Firebase env vars. Copy .env.example to .env.local and fill in ' +
      'VITE_FIREBASE_* values from the Firebase console (Project settings → Your apps).',
  );
}

export const firebaseApp: FirebaseApp = initializeApp(config);
export const firebaseAuth: Auth = getAuth(firebaseApp);

// Persist auth state in localStorage. Default in modern Firebase is
// indexedDBLocalPersistence which iOS Safari partitions / drops in some
// edge cases; localStorage survives the redirect more reliably. Fire-and-
// forget — `setPersistence` only fails if the storage backend is unavailable,
// which would have been logged elsewhere.
setPersistence(firebaseAuth, browserLocalPersistence).catch((e) => {
  console.warn('[firebase] setPersistence(browserLocal) failed', e);
});

export const googleProvider = new GoogleAuthProvider();
// Force account picker so users can switch Google accounts mid-session.
googleProvider.setCustomParameters({ prompt: 'select_account' });

// Optionally lock SSO to a specific Workspace domain.
const allowedDomain = import.meta.env.VITE_AUTH_ALLOWED_DOMAIN;
if (allowedDomain) {
  googleProvider.setCustomParameters({
    prompt: 'select_account',
    hd: allowedDomain,
  });
}
